import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiClock, FiUser, FiTag, FiShare2, FiBookmark, FiArrowLeft } from 'react-icons/fi';
import ContentRecommendations from './ContentRecommendations';
import CommentSection from './CommentSection';
import articleService from '../../services/articleService';

const ArticleContainer = styled.div`
  min-height: 100vh;
  padding-top: 80px;
  background: ${props => props.theme.colors.background};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => props.theme.spacing.xl};
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ArticleCategory = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: rgba(255, 107, 53, 0.2);
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => props.theme.spacing.lg};
  border: 1px solid rgba(255, 107, 53, 0.3);
`;

const ArticleTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
`;

const ArticleSubtitle = styled.h2`
  font-family: ${props => props.theme.fonts.body};
  font-weight: 400;
  font-size: 1.3rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: ${props => props.theme.spacing.xl};
  font-style: italic;
  text-transform: none;
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.divider};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    color: ${props => props.theme.colors.textSecondary};
    font-family: ${props => props.theme.fonts.accent};
    font-weight: 500;
    font-size: 0.9rem;
    
    .icon {
      color: ${props => props.theme.colors.primary};
    }
    
    .author {
      font-weight: 600;
      color: ${props => props.theme.colors.text};
    }
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 60vh;
  max-height: 600px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius.xl};
  margin-bottom: ${props => props.theme.spacing.xxl};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.theme.colors.cardShadow};
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 107, 53, 0.1), rgba(0, 212, 255, 0.1));
    opacity: 0;
    transition: opacity ${props => props.theme.transitions.normal};
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 40vh;
  }
`;

const ArticleActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xxl};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.divider};
  color: ${props => props.theme.colors.textSecondary};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const ArticleContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.textSecondary};
  
  p {
    margin-bottom: ${props => props.theme.spacing.xl};
    
    &:first-of-type {
      font-size: 1.3rem;
      color: ${props => props.theme.colors.text};
      font-weight: 500;
    }
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.lg};
    margin: ${props => props.theme.spacing.xxl} 0;
    box-shadow: ${props => props.theme.colors.cardShadow};
  }
  
  h2, h3, h4 {
    color: ${props => props.theme.colors.text};
    margin: ${props => props.theme.spacing.xxl} 0 ${props => props.theme.spacing.lg} 0;
  }
  
  blockquote {
    background: ${props => props.theme.colors.surface};
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding: ${props => props.theme.spacing.xl};
    margin: ${props => props.theme.spacing.xxl} 0;
    border-radius: ${props => props.theme.borderRadius.md};
    font-style: italic;
    font-size: 1.3rem;
    color: ${props => props.theme.colors.text};
    position: relative;
    
    &::before {
      content: '"';
      position: absolute;
      top: -10px;
      left: 20px;
      font-size: 4rem;
      color: ${props => props.theme.colors.primary};
      opacity: 0.3;
      font-family: ${props => props.theme.fonts.headline};
    }
  }
`;


const RelatedSection = styled.section`
  margin-top: ${props => props.theme.spacing.xxl};
  padding-top: ${props => props.theme.spacing.xxl};
  border-top: 1px solid ${props => props.theme.colors.divider};
`;

const RelatedTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.text};
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.divider};
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.colors.hoverShadow};
    border-color: ${props => props.theme.colors.primary};
  }
  
  .related-image {
    height: 200px;
    background-image: url(${props => props.image});
    background-size: cover;
    background-position: center;
    position: relative;
    
    .related-category {
      position: absolute;
      top: ${props => props.theme.spacing.md};
      left: ${props => props.theme.spacing.md};
      background: rgba(30, 30, 30, 0.8);
      color: ${props => props.theme.colors.primary};
      padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
      border-radius: ${props => props.theme.borderRadius.sm};
      font-family: ${props => props.theme.fonts.accent};
      font-weight: 600;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      backdrop-filter: blur(10px);
    }
  }
  
  .related-content {
    padding: ${props => props.theme.spacing.lg};
    
    h4 {
      font-size: 1.1rem;
      line-height: 1.4;
      margin-bottom: ${props => props.theme.spacing.sm};
      color: ${props => props.theme.colors.text};
    }
    
    .related-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 0.8rem;
      color: ${props => props.theme.colors.textSecondary};
      
      .author {
        font-weight: 600;
      }
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: ${props => props.theme.spacing.lg};
  
  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid ${props => props.theme.colors.divider};
    border-top: 3px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    font-family: ${props => props.theme.fonts.accent};
    font-weight: 500;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  
  h2 {
    color: ${props => props.theme.colors.error};
    margin-bottom: ${props => props.theme.spacing.lg};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: ${props => props.theme.spacing.xl};
  }
`;

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to fetch real article first, fall back to mock data
        let articleData;
        try {
          articleData = await articleService.getArticle(id);
          // Increment view count
          await articleService.incrementViews(id);
        } catch (fetchError) {
          // Fall back to mock data for demo
          articleData = {
            id: id,
            title: 'Spartans Win State Championship in Thrilling Overtime Victory',
            subtitle: 'In a nail-biting finish that had fans on the edge of their seats, the Schurr High Spartans secured their first state championship in over a decade with a dramatic 28-21 overtime victory.',
            image_url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1200&h=600&fit=crop',
            author: 'Sarah Johnson',
            published_at: new Date().toISOString(),
            section: 'Sports',
            read_time: '5 min read',
            views: 1247,
            likes: 23,
            content: `
              <p>The atmosphere was electric at Memorial Stadium last Friday night as the Schurr High Spartans faced off against their longtime rivals, the Central High Eagles, in what would become one of the most memorable championship games in school history.</p>
              
              <p>With both teams entering the game undefeated, expectations were sky-high. The Spartans, led by senior quarterback Marcus Thompson and a defense that had allowed fewer than 10 points per game all season, were determined to bring home their first state title since 2013.</p>
              
              <blockquote>
                <p>"We knew this was our moment. Four years of hard work, early morning practices, and believing in each other led to this game."</p>
                <cite>— Marcus Thompson, Senior Quarterback</cite>
              </blockquote>
              
              <p>The first half was a defensive battle, with both teams struggling to find their rhythm on offense. The Spartans managed to take a 7-3 lead into halftime thanks to a spectacular 45-yard touchdown pass from Thompson to wide receiver James Martinez with just two minutes left in the second quarter.</p>
              
              <p>The second half told a different story entirely. The Eagles came out firing, scoring 14 unanswered points in the third quarter to take a 17-7 lead. For a moment, it seemed like the Spartans' championship dreams might slip away.</p>
              
              <p>But this team had shown resilience all season long, and they weren't about to give up now. A crucial interception by safety David Rodriguez at the Eagles' 35-yard line set up a touchdown drive that brought the Spartans within three points. With 4:32 left in the fourth quarter, kicker Antonio Valdez nailed a 38-yard field goal to tie the game at 17-17.</p>
              
              <p>Neither team could score in the remaining regulation time, sending the championship game to overtime for the first time in tournament history.</p>
              
              <p>In overtime, the Eagles struck first with a touchdown on their opening possession. Facing a do-or-die situation, the Spartans responded with a methodical drive that culminated in a 12-yard touchdown run by running back Carlos Mendez. The extra point attempt was good, and suddenly the game was tied again.</p>
              
              <p>The second overtime period would prove to be the final chapter of this epic contest. The Spartans won the coin toss and elected to play defense first. Their decision paid off when linebacker Miguel Santos forced a fumble on the Eagles' second play, giving the Spartans possession at the 18-yard line.</p>
              
              <p>Three plays later, Thompson found Martinez in the corner of the end zone for the game-winning touchdown. As the final whistle blew, players, coaches, and fans rushed onto the field in celebration of a championship that had been 10 years in the making.</p>
              
              <blockquote>
                <p>"This group of seniors has been special from day one. They've shown incredible leadership and determination throughout their careers here."</p>
                <cite>— Coach Robert Wilson</cite>
              </blockquote>
              
              <p>The victory caps off a perfect 14-0 season for the Spartans and marks the culmination of a rebuilding process that began when Coach Wilson took over the program four years ago. The championship trophy will be displayed in the school's main hallway, serving as inspiration for future generations of Spartan athletes.</p>
              
              <p>For the seniors on this team, Friday night's victory represents the perfect ending to their high school careers. But for the underclassmen who contributed to this championship run, it's just the beginning of what could be a new era of Spartan football excellence.</p>
            `
          };
        }
        
        // Mock related articles
        const mockRelated = [
          {
            id: 'related-1',
            title: 'Season Recap: The Road to the Championship',
            image_url: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=400&h=300&fit=crop',
            author: 'Mike Chen',
            published_at: new Date().toISOString(),
            section: 'Sports'
          },
          {
            id: 'related-2',
            title: 'Player Spotlight: Marcus Thompson\'s Senior Year',
            image_url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop',
            author: 'Emma Davis',
            published_at: new Date().toISOString(),
            section: 'Sports'
          },
          {
            id: 'related-3',
            title: 'Coach Wilson Reflects on Championship Season',
            image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
            author: 'Alex Rodriguez',
            published_at: new Date().toISOString(),
            section: 'Sports'
          }
        ];
        
        setArticle(articleData);
        setRelatedArticles(mockRelated);
      } catch (err) {
        setError('Failed to load article');
        console.error('Error fetching article:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id]);
  
  if (loading) {
    return (
      <ArticleContainer>
        <div className="container">
          <LoadingState>
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </LoadingState>
        </div>
      </ArticleContainer>
    );
  }
  
  if (error || !article) {
    return (
      <ArticleContainer>
        <div className="container">
          <ErrorState>
            <h2>Article Not Found</h2>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/" className="btn btn-primary">
              Return Home
            </Link>
          </ErrorState>
        </div>
      </ArticleContainer>
    );
  }
  
  return (
    <ArticleContainer>
      <div className="container">
        <BackButton to="/">
          <FiArrowLeft size={16} />
          Back to Home
        </BackButton>
        
        <ArticleHeader>
          <ArticleCategory>
            <FiTag size={14} />
            {article.section}
          </ArticleCategory>
          
          <ArticleTitle>{article.title}</ArticleTitle>
          
          {article.subtitle && (
            <ArticleSubtitle>{article.subtitle}</ArticleSubtitle>
          )}
          
          <ArticleMeta>
            <div className="meta-item">
              <FiUser className="icon" size={16} />
              <span>By <span className="author">{article.author}</span></span>
            </div>
            <div className="meta-item">
              <FiClock className="icon" size={16} />
              <span>{new Date(article.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
            <div className="meta-item">
              <FiClock className="icon" size={16} />
              <span>{article.read_time}</span>
            </div>
          </ArticleMeta>
        </ArticleHeader>
        
        <HeroImage src={article.image_url} />
        
        <ArticleActions>
          <ActionButton>
            <FiShare2 size={16} />
            Share
          </ActionButton>
          <ActionButton>
            <FiBookmark size={16} />
            Save
          </ActionButton>
        </ArticleActions>
        
        <ArticleContent dangerouslySetInnerHTML={{ __html: article.content }} />
        
        <RelatedSection>
          <RelatedTitle>More in {article.section}</RelatedTitle>
          <RelatedGrid>
            {relatedArticles.map(related => (
              <RelatedCard key={related.id} to={`/article/${related.id}`} image={related.image_url}>
                <div className="related-image">
                  <div className="related-category">{related.section}</div>
                </div>
                <div className="related-content">
                  <h4>{related.title}</h4>
                  <div className="related-meta">
                    <span className="author">By {related.author}</span>
                    <span>{new Date(related.published_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}</span>
                  </div>
                </div>
              </RelatedCard>
            ))}
          </RelatedGrid>
        </RelatedSection>
        
        <ContentRecommendations currentArticle={article} />
        
        <CommentSection articleId={article.id} />
      </div>
    </ArticleContainer>
  );
};

export default ArticlePage;